import { React, useEffect, useMemo, useRef, useState } from '../../shared/react.js';
import { Icon } from '../../shared/icons.js';
import { useAppRuntime, useAppState } from '../../app/app.context.js';
import type { ArtworkRecord, ClusterRecord } from '../../../../../packages/contracts/src/search.js';
import type { QueryFilterField } from '../../app/app.types.js';

interface FilterOption {
  readonly field: QueryFilterField;
  readonly value: string | number;
  readonly label: string;
  readonly group: string;
}

export function QueryFilterMenu(props: { readonly anchor: HTMLButtonElement | null; readonly onClose: () => void }) {
  const runtime = useAppRuntime();
  const state = useAppState();
  const [query, setQuery] = useState('');
  const menuRef = useRef(null as HTMLDivElement | null);
  const searchRef = useRef(null as HTMLInputElement | null);
  const closeRef = useRef(props.onClose);
  closeRef.current = props.onClose;
  const options = useMemo(() => buildOptions(state.corpus?.items ?? [], state.corpus?.clusters ?? []), [state.corpus]) as readonly FilterOption[];
  const normalized = query.trim().toLowerCase();
  const visible: readonly FilterOption[] = options.filter((option: FilterOption) => !normalized || `${option.label} ${option.group}`.toLowerCase().includes(normalized)).slice(0, 28);

  useEffect(() => {
    const menu = menuRef.current as HTMLDivElement | null;
    const anchor = props.anchor;
    if (!menu || !anchor) return;
    const reposition = () => {
      const viewport = window.visualViewport;
      const width = viewport?.width ?? innerWidth;
      const height = viewport?.height ?? innerHeight;
      const left = viewport?.offsetLeft ?? 0;
      const top = viewport?.offsetTop ?? 0;
      const mobile = width <= 680;
      menu.classList.toggle('query-filter-menu--sheet', mobile);
      menu.setAttribute('aria-modal', String(mobile));
      menu.style.width = `${Math.min(304, width - 24)}px`;
      menu.style.maxHeight = `${Math.min(440, height - 24)}px`;
      const rect = anchor.getBoundingClientRect();
      const menuHeight = menu.getBoundingClientRect().height;
      let x = Math.max(left + 12, Math.min(rect.left, left + width - menu.offsetWidth - 12));
      let y = rect.bottom + 8;
      if (mobile) {
        menu.style.width = `${width - 24}px`;
        menu.style.maxHeight = `${Math.min(440, height * .72)}px`;
        x = left + 12;
        y = top + height - menu.getBoundingClientRect().height - 12;
      } else if (y + menuHeight > top + height - 12) {
        const above = rect.top - top - 20;
        const below = top + height - rect.bottom - 20;
        if (above > below) {
          menu.style.maxHeight = `${Math.min(440, Math.max(120, above))}px`;
          y = rect.top - menu.getBoundingClientRect().height - 8;
        } else {
          menu.style.maxHeight = `${Math.min(440, Math.max(120, below))}px`;
        }
      }
      menu.style.left = `${x}px`;
      menu.style.top = `${Math.max(top + 12, Math.min(y, top + height - menu.getBoundingClientRect().height - 12))}px`;
    };
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!menu.contains(target) && !anchor.contains(target)) {
        // Prevent the pointer's default focus change from overriding focus return.
        event.preventDefault();
        closeRef.current();
      }
    };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
      } else if (event.key === 'Tab') {
        const controls = Array.from(menu.querySelectorAll<HTMLElement>('button:not(:disabled), input'));
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const buttons = Array.from(menu.querySelectorAll<HTMLButtonElement>('.query-filter-menu__options button'));
        if (!buttons.length) return;
        event.preventDefault();
        const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
        const next = index < 0 ? (event.key === 'ArrowDown' ? 0 : buttons.length - 1) : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
        buttons[next]?.focus();
      }
    };
    reposition();
    searchRef.current?.focus({ preventScroll: true });
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', keyboard, true);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    window.visualViewport?.addEventListener('resize', reposition);
    window.visualViewport?.addEventListener('scroll', reposition);
    const observer = new ResizeObserver(reposition);
    observer.observe(anchor);
    observer.observe(menu);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', keyboard, true);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      window.visualViewport?.removeEventListener('resize', reposition);
      window.visualViewport?.removeEventListener('scroll', reposition);
      observer.disconnect();
      if (anchor.isConnected) anchor.focus({ preventScroll: true });
    };
  }, [props.anchor]);

  const choose = (option: FilterOption) => {
    void runtime.addFilter(option.field, option.value, option.label);
    props.onClose();
  };

  return window.SHAP.ReactDOM.createPortal(<>
    <div className="query-filter-backdrop" aria-hidden="true"/>
    <div ref={menuRef} className="query-filter-menu" role="dialog" aria-label="Add a collection filter">
      <div className="query-filter-menu__top">
        <strong>Refine the collection</strong>
        <button type="button" className="icon-button" onClick={props.onClose} aria-label="Close filter menu"><Icon name="close" size={14}/></button>
      </div>
      <label className="query-filter-menu__search">
        <Icon name="search" size={14}/>
        <input ref={searchRef} value={query} onInput={(event: any) => setQuery(event.currentTarget.value)} placeholder="Culture, medium, date…" aria-label="Find a filter"/>
      </label>
      <div className="query-filter-menu__options">
        {visible.map((option: FilterOption) => (
          <button type="button" key={`${option.field}:${option.value}`} aria-pressed={state.queryFilters.some(filter => filter.field === option.field && filter.value === option.value)} onClick={() => choose(option)}>
            <span>{option.label}</span><small>{option.group}</small>
          </button>
        ))}
      </div>
      {visible.length === 0 && <p className="query-filter-menu__empty">No matching filters</p>}
      {state.queryFilters.length > 0 && (
        <button type="button" className="query-filter-menu__clear" onClick={() => { void runtime.clearFilters(); props.onClose(); }}>Clear all filters</button>
      )}
    </div>
  </>, document.body);
}

function buildOptions(items: readonly ArtworkRecord[], clusters: readonly ClusterRecord[]): readonly FilterOption[] {
  const options: FilterOption[] = [
    { field: 'before', value: 1900, label: 'Before 1900', group: 'Date' },
    { field: 'before', value: 1800, label: 'Before 1800', group: 'Date' },
    { field: 'after', value: 1900, label: 'After 1900', group: 'Date' },
    ...clusters.map((cluster) => ({ field: 'cluster' as const, value: cluster.id, label: cluster.label, group: 'Neighborhood' }))
  ];
  const fields = [
    ['culture', 'Culture'],
    ['department', 'Department'],
    ['classification', 'Classification'],
    ['medium', 'Medium']
  ] as const;
  for (const [field, group] of fields) {
    const counts = new Map<string, number>();
    for (const item of items) {
      const value = String(item[field] ?? '').trim();
      if (!value) continue;
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    const common = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, field === 'medium' ? 8 : 12);
    for (const [value] of common) options.push({ field, value, label: value, group });
  }
  return options;
}
